import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import useActivationDevices from '@/src/Activations/components/hooks/useActivationDevices';
import useDeviceTelemetry, {
    PrintRollup,
    QueueData,
} from '@/src/DeviceTelemetry/hooks/useDeviceTelemetry';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useGetGalleries from '@/src/GalleryConfiguration/components/hooks/useGetGalleries';
import useDeviceKeys from '@/src/DeviceManagement/components/hooks/useDeviceKeys';
import FeatureFlagContext from '@/src/Features/components/FeatureFlagContext';
import { FeatureFlags } from '@/src/Features/lib/constants';
import { useContext } from 'react';

/*
 * EventDevices — the live-ops strip on the event Overview: every device
 * uploading to THIS event, with uploaded/remaining/prints, friendly-name
 * inline edit, and Remote/Telemetry links. Replaces the classic details
 * tab's Queue Data card (the #1 unbridged gap in the parity map).
 * Devices refresh via the hook's 30s poll; queue + prints poll at 10s.
 */

const ACTIVE_WINDOW_MIN = 3;
const STUCK_WINDOW_MIN = 10;

interface Row {
    deviceKeyId: string;
    telemetryDeviceId?: string;
    displayName: string;
    friendlyName: string | null;
    uploaded?: number;
    remaining?: number;
    prints?: number;
    lastActivity: string | null;
}

export default function EventDevices({
    activationId,
}: {
    activationId: number;
}) {
    const navigate = useNavigate();
    const { orgId } = useUserOrg();
    const { devices, updateFriendlyName } =
        useActivationDevices(activationId);
    const { getQueueDataForActivation, getPrintRollupForActivation } =
        useDeviceTelemetry();
    const authenticatedApi = useAuthenticatedApi();
    const { getGalleriesForActivation } = useGetGalleries();
    const { getDeviceKeys } = useDeviceKeys(orgId);

    const [queue, setQueue] = useState<QueueData[]>([]);
    const [rollup, setRollup] = useState<PrintRollup>({
        total: 0,
        devices: [],
    });
    const [editing, setEditing] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [galleryId, setGalleryId] = useState<number | null>(null);
    const [allDevices, setAllDevices] = useState<any[]>([]);
    const [allGroups, setAllGroups] = useState<any[]>([]);
    const [assignedDevices, setAssignedDevices] = useState<number[]>([]);
    const [assignedGroups, setAssignedGroups] = useState<number[]>([]);
    const [assignEdit, setAssignEdit] = useState(false);
    const [draftDevices, setDraftDevices] = useState<Set<number>>(new Set());
    const [draftGroups, setDraftGroups] = useState<Set<number>>(new Set());
    const [assignBusy, setAssignBusy] = useState(false);
    const [assignError, setAssignError] = useState(false);
    /* names straight from the assignment rows — covers borrowed devices
       whose owner-org lists we don't have */
    const [assignedNames, setAssignedNames] = useState<
        Record<string, string>
    >({});
    /* cross-org borrowing (GlobalAdmin) */
    const featureFlags = useContext(FeatureFlagContext) as any;
    const isGlobalAdmin = !!featureFlags?.[FeatureFlags.GLOBAL_ADMIN_V1];
    const [orgs, setOrgs] = useState<any[] | null>(null);
    const [borrowOrgId, setBorrowOrgId] = useState('');
    const [orgCatalog, setOrgCatalog] = useState<
        Record<number, { devices: any[]; groups: any[] }>
    >({});
    /* selections persist per-org across org switches (classic loses them) */
    const [crossDevices, setCrossDevices] = useState<
        Record<number, Set<number>>
    >({});
    const [crossGroups, setCrossGroups] = useState<
        Record<number, Set<number>>
    >({});
    const [keptBorrowedDevices, setKeptBorrowedDevices] = useState<
        Set<number>
    >(new Set());
    const [keptBorrowedGroups, setKeptBorrowedGroups] = useState<
        Set<number>
    >(new Set());

    const loadBorrowOrg = async (oid: number) => {
        if (orgCatalog[oid]) return;
        const [keys, groups] = await Promise.all([
            authenticatedApi
                .get(`${oid}/device-keys`)
                .then((r) => r.data)
                .catch(() => []),
            authenticatedApi
                .get(`${oid}/device-groups`)
                .then((r) => r.data)
                .catch(() => []),
        ]);
        setOrgCatalog((c) => ({
            ...c,
            [oid]: { devices: keys ?? [], groups: groups ?? [] },
        }));
    };

    useEffect(() => {
        if (!orgId || !activationId) return;
        let mounted = true;
        (async () => {
            const galleries = await getGalleriesForActivation(
                activationId,
            ).catch(() => []);
            const gid = galleries?.[0]?.id ?? null;
            if (!mounted) return;
            setGalleryId(gid);
            if (gid == null) return;
            const [devRows, grpRows, keys, groups] = await Promise.all([
                authenticatedApi
                    .get(`/device-management/assignments/gallery/${gid}`)
                    .then((r) => r.data)
                    .catch(() => []),
                authenticatedApi
                    .get(
                        `/${orgId}/device-groups/assignments/gallery/${gid}`,
                    )
                    .then((r) => r.data)
                    .catch(() => []),
                getDeviceKeys().catch(() => []),
                authenticatedApi
                    .get(`${orgId}/device-groups`)
                    .then((r) => r.data)
                    .catch(() => []),
            ]);
            if (!mounted) return;
            const names: Record<string, string> = {};
            (devRows ?? []).forEach((r: any) => {
                const did = +(r.deviceKeyId ?? r.device_key_id);
                if (Number.isFinite(did) && (r.deviceName ?? r.name))
                    names[`d${did}`] = r.deviceName ?? r.name;
            });
            (grpRows ?? []).forEach((r: any) => {
                const gid2 = +(r.groupId ?? r.group_id);
                if (Number.isFinite(gid2) && r.name)
                    names[`g${gid2}`] = r.name;
            });
            setAssignedNames(names);
            setAssignedDevices(
                (devRows ?? [])
                    .map((r: any) => +(r.deviceKeyId ?? r.device_key_id))
                    .filter((n: number) => Number.isFinite(n)),
            );
            setAssignedGroups(
                (grpRows ?? [])
                    .map((r: any) => +(r.groupId ?? r.group_id))
                    .filter((n: number) => Number.isFinite(n)),
            );
            setAllDevices(keys ?? []);
            setAllGroups(groups ?? []);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, activationId]);

    const saveAssignments = async () => {
        if (galleryId == null || assignBusy) return;
        setAssignBusy(true);
        setAssignError(false);
        /* own-org ids never ride in crossOrg entries (classic mixed them
           up); kept-borrowed ids ride under orgId 0 — the server dedupes
           into one Set and ignores the orgId attribution */
        const crossDeviceEntries = Object.entries(crossDevices)
            .filter(([, set]) => set.size > 0)
            .map(([oid, set]) => ({
                orgId: +oid,
                deviceIds: Array.from(set),
            }));
        const alreadyPicked = new Set(
            crossDeviceEntries.flatMap((e) => e.deviceIds),
        );
        const keptDev = Array.from(keptBorrowedDevices).filter(
            (d) => !alreadyPicked.has(d),
        );
        if (keptDev.length > 0)
            crossDeviceEntries.push({ orgId: 0, deviceIds: keptDev });

        const crossGroupEntries = Object.entries(crossGroups)
            .filter(([, set]) => set.size > 0)
            .map(([oid, set]) => ({
                orgId: +oid,
                deviceGroups: Array.from(set),
            }));
        const pickedGroups = new Set(
            crossGroupEntries.flatMap((e) => e.deviceGroups),
        );
        const keptGrp = Array.from(keptBorrowedGroups).filter(
            (g) => !pickedGroups.has(g),
        );
        if (keptGrp.length > 0)
            crossGroupEntries.push({ orgId: 0, deviceGroups: keptGrp });

        const results = await Promise.all([
            authenticatedApi
                .post(
                    `/device-management/assignments/gallery/${galleryId}/create`,
                    {
                        deviceIds: Array.from(draftDevices),
                        ...(isGlobalAdmin && crossDeviceEntries.length > 0
                            ? { crossOrgDeviceIds: crossDeviceEntries }
                            : {}),
                    },
                )
                .catch(() => null),
            authenticatedApi
                .post(`/${orgId}/device-groups/assignments/gallery`, {
                    galleryId,
                    deviceGroups: Array.from(draftGroups),
                    ...(isGlobalAdmin && crossGroupEntries.length > 0
                        ? { crossOrgDeviceGroups: crossGroupEntries }
                        : {}),
                })
                .catch(() => null),
        ]);
        setAssignBusy(false);
        if (results.every((r) => r != null)) {
            setAssignEdit(false);
            /* re-read from the server — borrowed names/ids come from the
               assignment rows */
            const [devRows, grpRows] = await Promise.all([
                authenticatedApi
                    .get(
                        `/device-management/assignments/gallery/${galleryId}`,
                    )
                    .then((r) => r.data)
                    .catch(() => []),
                authenticatedApi
                    .get(
                        `/${orgId}/device-groups/assignments/gallery/${galleryId}`,
                    )
                    .then((r) => r.data)
                    .catch(() => []),
            ]);
            const names: Record<string, string> = {};
            (devRows ?? []).forEach((r: any) => {
                const did = +(r.deviceKeyId ?? r.device_key_id);
                if (Number.isFinite(did) && (r.deviceName ?? r.name))
                    names[`d${did}`] = r.deviceName ?? r.name;
            });
            (grpRows ?? []).forEach((r: any) => {
                const gid2 = +(r.groupId ?? r.group_id);
                if (Number.isFinite(gid2) && r.name)
                    names[`g${gid2}`] = r.name;
            });
            setAssignedNames(names);
            setAssignedDevices(
                (devRows ?? [])
                    .map((r: any) => +(r.deviceKeyId ?? r.device_key_id))
                    .filter((n: number) => Number.isFinite(n)),
            );
            setAssignedGroups(
                (grpRows ?? [])
                    .map((r: any) => +(r.groupId ?? r.group_id))
                    .filter((n: number) => Number.isFinite(n)),
            );
        } else {
            setAssignError(true);
        }
    };

    useEffect(() => {
        if (!orgId || !activationId) return;
        let mounted = true;
        const load = async () => {
            const [q, r] = await Promise.all([
                getQueueDataForActivation(orgId, activationId).catch(
                    () => [],
                ),
                getPrintRollupForActivation(orgId, activationId).catch(
                    () => ({ total: 0, devices: [] }),
                ),
            ]);
            if (!mounted) return;
            setQueue(q ?? []);
            setRollup(r ?? { total: 0, devices: [] });
        };
        load();
        const timer = setInterval(load, 10 * 1000);
        return () => {
            mounted = false;
            clearInterval(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, activationId]);

    const rows = useMemo<Row[]>(() => {
        const prints = new Map(
            rollup.devices.map((d) => [d.deviceKeyId, d.prints]),
        );
        const byKey = new Map<string, Row>();
        devices.forEach((d) => {
            const q = queue.find((x) => x.deviceKeyId === d.deviceKeyId);
            const qd = (q?.queueData ?? {}) as {
                uploaded?: number;
                unuploaded?: number;
            };
            byKey.set(d.deviceKeyId, {
                deviceKeyId: d.deviceKeyId,
                telemetryDeviceId: q?.deviceId,
                displayName: d.displayName,
                friendlyName: d.friendlyName,
                uploaded: qd.uploaded,
                remaining: qd.unuploaded,
                prints: prints.get(d.deviceKeyId),
                lastActivity:
                    [d.lastUploadAt, q?.updatedAt]
                        .filter(Boolean)
                        .sort()
                        .pop() ?? null,
            });
        });
        queue.forEach((q) => {
            if (byKey.has(q.deviceKeyId)) return;
            const qd = (q.queueData ?? {}) as {
                uploaded?: number;
                unuploaded?: number;
            };
            byKey.set(q.deviceKeyId, {
                deviceKeyId: q.deviceKeyId,
                telemetryDeviceId: q.deviceId,
                displayName: (q as any).name || 'Unknown device',
                friendlyName: null,
                uploaded: qd.uploaded,
                remaining: qd.unuploaded,
                prints: prints.get(q.deviceKeyId),
                lastActivity: q.updatedAt ?? null,
            });
        });
        rollup.devices.forEach((d) => {
            if (byKey.has(d.deviceKeyId)) return;
            byKey.set(d.deviceKeyId, {
                deviceKeyId: d.deviceKeyId,
                displayName: 'Unknown device',
                friendlyName: null,
                prints: d.prints,
                lastActivity: null,
            });
        });
        return Array.from(byKey.values()).sort(
            (a, b) =>
                moment(b.lastActivity ?? 0).valueOf() -
                moment(a.lastActivity ?? 0).valueOf(),
        );
    }, [devices, queue, rollup]);

    const saveEdit = async (row: Row) => {
        const value = editValue.trim();
        setEditing(null);
        if (!value || value === (row.friendlyName ?? row.displayName))
            return;
        await updateFriendlyName(row.deviceKeyId, value);
    };

    /* assigned-but-quiet: assigned devices with no activity rows yet */
    const activeKeys = new Set(rows.map((r) => r.deviceKeyId));
    const quietDevices = assignedDevices.filter((did) => {
        const key = allDevices.find((d: any) => d.id === did)?.keyId;
        return !key || !activeKeys.has(key);
    });

    if (
        rows.length === 0 &&
        assignedDevices.length === 0 &&
        assignedGroups.length === 0 &&
        !assignEdit &&
        galleryId == null
    )
        return null;

    return (
        <section className="mb-10">
            <div className="mb-1.5 flex items-baseline justify-between">
                <span className="ps-label">Devices on this event</span>
                <span className="flex items-baseline gap-3.5">
                    {rollup.total > 0 && (
                        <span className="text-[12px] text-muted-foreground">
                            <b className="font-semibold text-foreground tabular-nums">
                                {rollup.total.toLocaleString()}
                            </b>{' '}
                            print{rollup.total === 1 ? '' : 's'} this event
                        </span>
                    )}
                    {!assignEdit ? (
                        <button
                            type="button"
                            onClick={() => {
                                const ownDeviceIds = new Set(
                                    allDevices.map((d2: any) => d2.id),
                                );
                                const ownGroupIds = new Set(
                                    allGroups.map((g2: any) => g2.id),
                                );
                                setDraftDevices(
                                    new Set(
                                        assignedDevices.filter((d2) =>
                                            ownDeviceIds.has(d2),
                                        ),
                                    ),
                                );
                                setKeptBorrowedDevices(
                                    new Set(
                                        assignedDevices.filter(
                                            (d2) => !ownDeviceIds.has(d2),
                                        ),
                                    ),
                                );
                                setDraftGroups(
                                    new Set(
                                        assignedGroups.filter((g2) =>
                                            ownGroupIds.has(g2),
                                        ),
                                    ),
                                );
                                setKeptBorrowedGroups(
                                    new Set(
                                        assignedGroups.filter(
                                            (g2) => !ownGroupIds.has(g2),
                                        ),
                                    ),
                                );
                                setCrossDevices({});
                                setCrossGroups({});
                                setBorrowOrgId('');
                                setAssignEdit(true);
                                if (isGlobalAdmin && orgs == null)
                                    authenticatedApi
                                        .get('/orgs')
                                        .then((r) =>
                                            setOrgs(r.data ?? []),
                                        )
                                        .catch(() => setOrgs([]));
                            }}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                        >
                            Assign
                        </button>
                    ) : (
                        <span className="flex items-center gap-2.5">
                            {assignError && (
                                <span className="text-[11px] font-semibold text-attention">
                                    Save failed
                                </span>
                            )}
                            <button
                                type="button"
                                disabled={assignBusy}
                                onClick={saveAssignments}
                                className="cursor-pointer rounded-full border-0 bg-brand px-3.5 py-1 font-[inherit] text-[11.5px] font-semibold text-white"
                            >
                                {assignBusy ? 'Saving…' : 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setAssignEdit(false)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                        </span>
                    )}
                </span>
            </div>
            {assignEdit && (
                <div className="mb-3 grid gap-x-6 gap-y-3 rounded-[12px] border border-border p-3.5 sm:grid-cols-2">
                    <div>
                        <div className="mb-1 text-[11px] font-semibold text-muted-foreground">
                            Groups
                        </div>
                        <div className="max-h-[160px] overflow-y-auto">
                            {allGroups.map((g2: any) => (
                                <label
                                    key={g2.id}
                                    className="flex cursor-pointer items-center gap-2 py-1 text-[12.5px]"
                                >
                                    <input
                                        type="checkbox"
                                        checked={draftGroups.has(g2.id)}
                                        onChange={() =>
                                            setDraftGroups((prev) => {
                                                const next = new Set(prev);
                                                next.has(g2.id)
                                                    ? next.delete(g2.id)
                                                    : next.add(g2.id);
                                                return next;
                                            })
                                        }
                                    />
                                    <span className="truncate">
                                        {g2.name}
                                    </span>
                                </label>
                            ))}
                            {allGroups.length === 0 && (
                                <span className="text-[12px] text-faint">
                                    No groups.
                                </span>
                            )}
                        </div>
                    </div>
                    <div>
                        <div className="mb-1 text-[11px] font-semibold text-muted-foreground">
                            Devices
                        </div>
                        <div className="max-h-[160px] overflow-y-auto">
                            {allDevices.map((d2: any) => (
                                <label
                                    key={d2.id}
                                    className="flex cursor-pointer items-center gap-2 py-1 text-[12.5px]"
                                >
                                    <input
                                        type="checkbox"
                                        checked={draftDevices.has(d2.id)}
                                        onChange={() =>
                                            setDraftDevices((prev) => {
                                                const next = new Set(prev);
                                                next.has(d2.id)
                                                    ? next.delete(d2.id)
                                                    : next.add(d2.id);
                                                return next;
                                            })
                                        }
                                    />
                                    <span className="truncate">
                                        {d2.name}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                    {isGlobalAdmin && (
                        <div className="sm:col-span-2 border-t border-line pt-3">
                            <div className="mb-1.5 flex flex-wrap items-center gap-2">
                                <span className="text-[11px] font-semibold text-muted-foreground">
                                    From other organizations
                                </span>
                                <select
                                    className="ps-input w-[200px] cursor-pointer appearance-none text-[12px]"
                                    value={borrowOrgId}
                                    onChange={(e) => {
                                        setBorrowOrgId(e.target.value);
                                        if (e.target.value)
                                            loadBorrowOrg(+e.target.value);
                                    }}
                                >
                                    <option value="">Borrow from…</option>
                                    {(orgs ?? [])
                                        .filter(
                                            (o: any) => o.id !== orgId,
                                        )
                                        .map((o: any) => (
                                            <option
                                                key={o.id}
                                                value={o.id}
                                            >
                                                {o.name}
                                            </option>
                                        ))}
                                </select>
                                {(keptBorrowedDevices.size > 0 ||
                                    keptBorrowedGroups.size > 0) && (
                                    <span className="flex flex-wrap gap-1.5">
                                        {Array.from(
                                            keptBorrowedGroups,
                                        ).map((gid2) => (
                                            <span
                                                key={`kg${gid2}`}
                                                className="inline-flex items-center gap-1 rounded-full bg-secondary py-[2px] pl-2 pr-1 text-[11px] font-semibold text-muted-foreground"
                                            >
                                                {assignedNames[
                                                    `g${gid2}`
                                                ] ?? `group ${gid2}`}
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setKeptBorrowedGroups(
                                                            (prev) => {
                                                                const n2 =
                                                                    new Set(
                                                                        prev,
                                                                    );
                                                                n2.delete(
                                                                    gid2,
                                                                );
                                                                return n2;
                                                            },
                                                        )
                                                    }
                                                    className="cursor-pointer border-0 bg-transparent px-0.5 text-faint hover:text-attention"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                        {Array.from(
                                            keptBorrowedDevices,
                                        ).map((did) => (
                                            <span
                                                key={`kd${did}`}
                                                className="inline-flex items-center gap-1 rounded-full border border-border py-[2px] pl-2 pr-1 text-[11px] text-muted-foreground"
                                            >
                                                {assignedNames[
                                                    `d${did}`
                                                ] ?? `device ${did}`}
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setKeptBorrowedDevices(
                                                            (prev) => {
                                                                const n2 =
                                                                    new Set(
                                                                        prev,
                                                                    );
                                                                n2.delete(
                                                                    did,
                                                                );
                                                                return n2;
                                                            },
                                                        )
                                                    }
                                                    className="cursor-pointer border-0 bg-transparent px-0.5 text-faint hover:text-attention"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </span>
                                )}
                            </div>
                            {borrowOrgId && (
                                <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                                    <div>
                                        <div className="mb-1 text-[11px] font-semibold text-faint">
                                            Groups
                                        </div>
                                        <div className="max-h-[130px] overflow-y-auto">
                                            {(
                                                orgCatalog[+borrowOrgId]
                                                    ?.groups ?? []
                                            ).map((g2: any) => (
                                                <label
                                                    key={g2.id}
                                                    className="flex cursor-pointer items-center gap-2 py-1 text-[12.5px]"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            crossGroups[
                                                                +borrowOrgId
                                                            ]?.has(g2.id) ??
                                                            false
                                                        }
                                                        onChange={() =>
                                                            setCrossGroups(
                                                                (prev) => {
                                                                    const set =
                                                                        new Set(
                                                                            prev[
                                                                                +borrowOrgId
                                                                            ] ??
                                                                                [],
                                                                        );
                                                                    set.has(
                                                                        g2.id,
                                                                    )
                                                                        ? set.delete(
                                                                              g2.id,
                                                                          )
                                                                        : set.add(
                                                                              g2.id,
                                                                          );
                                                                    return {
                                                                        ...prev,
                                                                        [+borrowOrgId]:
                                                                            set,
                                                                    };
                                                                },
                                                            )
                                                        }
                                                    />
                                                    <span className="truncate">
                                                        {g2.name}
                                                    </span>
                                                </label>
                                            ))}
                                            {orgCatalog[+borrowOrgId] &&
                                                orgCatalog[+borrowOrgId]
                                                    .groups.length === 0 && (
                                                    <span className="text-[11.5px] text-faint">
                                                        No groups.
                                                    </span>
                                                )}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="mb-1 text-[11px] font-semibold text-faint">
                                            Devices
                                        </div>
                                        <div className="max-h-[130px] overflow-y-auto">
                                            {!orgCatalog[+borrowOrgId] && (
                                                <span className="text-[11.5px] text-faint">
                                                    Loading…
                                                </span>
                                            )}
                                            {(
                                                orgCatalog[+borrowOrgId]
                                                    ?.devices ?? []
                                            ).map((d2: any) => (
                                                <label
                                                    key={d2.id}
                                                    className="flex cursor-pointer items-center gap-2 py-1 text-[12.5px]"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            crossDevices[
                                                                +borrowOrgId
                                                            ]?.has(d2.id) ??
                                                            false
                                                        }
                                                        onChange={() =>
                                                            setCrossDevices(
                                                                (prev) => {
                                                                    const set =
                                                                        new Set(
                                                                            prev[
                                                                                +borrowOrgId
                                                                            ] ??
                                                                                [],
                                                                        );
                                                                    set.has(
                                                                        d2.id,
                                                                    )
                                                                        ? set.delete(
                                                                              d2.id,
                                                                          )
                                                                        : set.add(
                                                                              d2.id,
                                                                          );
                                                                    return {
                                                                        ...prev,
                                                                        [+borrowOrgId]:
                                                                            set,
                                                                    };
                                                                },
                                                            )
                                                        }
                                                    />
                                                    <span className="truncate">
                                                        {d2.name}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
            {rows.map((row, i) => {
                const minsSince = row.lastActivity
                    ? moment().diff(moment(row.lastActivity), 'minutes')
                    : null;
                const active =
                    minsSince != null && minsSince < ACTIVE_WINDOW_MIN;
                const stuck =
                    (row.remaining ?? 0) > 0 &&
                    (minsSince == null || minsSince > STUCK_WINDOW_MIN);
                return (
                    <div
                        key={row.deviceKeyId}
                        className={
                            'group flex items-center gap-3 py-2.5 ' +
                            (i > 0 ? 'border-t border-line' : '')
                        }
                    >
                        <span
                            className={
                                'h-2 w-2 flex-none rounded-full ' +
                                (active ? 'ps-pulse' : 'bg-off')
                            }
                        />
                        <div className="w-[210px] flex-none">
                            {editing === row.deviceKeyId ? (
                                <input
                                    autoFocus
                                    className="ps-input w-full text-[13px] font-semibold"
                                    value={editValue}
                                    onChange={(e) =>
                                        setEditValue(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveEdit(row);
                                        if (e.key === 'Escape')
                                            setEditing(null);
                                    }}
                                    onBlur={() => saveEdit(row)}
                                />
                            ) : (
                                <span
                                    className={
                                        'flex items-center gap-1.5 text-[13px] font-semibold ' +
                                        (active ? '' : 'text-muted-foreground')
                                    }
                                >
                                    <span className="truncate">
                                        {row.displayName}
                                    </span>
                                    <button
                                        type="button"
                                        title="Rename for this event"
                                        onClick={() => {
                                            setEditing(row.deviceKeyId);
                                            setEditValue(
                                                row.friendlyName ??
                                                    row.displayName,
                                            );
                                        }}
                                        className="cursor-pointer border-0 bg-transparent p-0 text-[11px] text-faint opacity-0 group-hover:opacity-100"
                                    >
                                        ✎
                                    </button>
                                </span>
                            )}
                        </div>
                        <div className="flex min-w-0 flex-1 flex-wrap gap-x-5 gap-y-1">
                            {[
                                {
                                    label: 'Uploaded',
                                    value: row.uploaded,
                                    warn: false,
                                },
                                {
                                    label: 'Remaining',
                                    value: row.remaining,
                                    warn: stuck,
                                },
                                {
                                    label: 'Prints',
                                    value: row.prints,
                                    warn: false,
                                },
                            ]
                                .filter((c) => c.value != null)
                                .map((c) => (
                                    <span
                                        key={c.label}
                                        className="whitespace-nowrap text-[12.5px]"
                                    >
                                        <b
                                            className={
                                                'font-semibold tabular-nums ' +
                                                (c.warn
                                                    ? 'text-attention'
                                                    : '')
                                            }
                                        >
                                            {Number(
                                                c.value,
                                            ).toLocaleString()}
                                        </b>{' '}
                                        <span
                                            className={
                                                'font-mono text-[9px] uppercase tracking-[0.1em] ' +
                                                (c.warn
                                                    ? 'font-semibold text-attention'
                                                    : 'text-faint')
                                            }
                                        >
                                            {c.label}
                                        </span>
                                    </span>
                                ))}
                        </div>
                        <span className="ml-auto flex flex-none items-center gap-3.5">
                            <span className="whitespace-nowrap text-[11.5px] text-faint">
                                {row.lastActivity
                                    ? moment(row.lastActivity).fromNow()
                                    : '—'}
                            </span>
                            {active && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        navigate(
                                            `/orgs/${orgId}/devices/${row.deviceKeyId}/remote`,
                                        )
                                    }
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                                >
                                    Remote
                                </button>
                            )}
                            {row.telemetryDeviceId && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        navigate(
                                            `/orgs/${orgId}/device-telemetry/${row.telemetryDeviceId}`,
                                        )
                                    }
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                                >
                                    Telemetry
                                </button>
                            )}
                        </span>
                    </div>
                );
            })}
            {!assignEdit &&
                (assignedGroups.length > 0 || quietDevices.length > 0) && (
                    <div
                        className={
                            'flex flex-wrap gap-1.5 ' +
                            (rows.length > 0
                                ? 'border-t border-line pt-2.5'
                                : '')
                        }
                    >
                        {assignedGroups.map((gid2) => {
                            const own = allGroups.some(
                                (g2: any) => g2.id === gid2,
                            );
                            return (
                                <span
                                    key={`g${gid2}`}
                                    className="rounded-full bg-secondary px-2.5 py-[3px] text-[11.5px] font-semibold text-muted-foreground"
                                >
                                    {assignedNames[`g${gid2}`] ??
                                        allGroups.find(
                                            (g2: any) => g2.id === gid2,
                                        )?.name ??
                                        `group ${gid2}`}
                                    <span className="ml-1 font-mono text-[8px] uppercase text-faint">
                                        {own ? 'group' : 'group · borrowed'}
                                    </span>
                                </span>
                            );
                        })}
                        {quietDevices.map((did) => {
                            const own = allDevices.some(
                                (d2: any) => d2.id === did,
                            );
                            return (
                                <span
                                    key={`d${did}`}
                                    className="rounded-full border border-border px-2.5 py-[3px] text-[11.5px] text-muted-foreground"
                                >
                                    {assignedNames[`d${did}`] ??
                                        allDevices.find(
                                            (d2: any) => d2.id === did,
                                        )?.name ??
                                        `device ${did}`}
                                    <span className="ml-1 font-mono text-[8px] uppercase text-faint">
                                        {own ? 'assigned' : 'borrowed'}
                                    </span>
                                </span>
                            );
                        })}
                    </div>
                )}
            {!assignEdit &&
                rows.length === 0 &&
                assignedDevices.length === 0 &&
                assignedGroups.length === 0 && (
                    <p className="py-1 text-[13px] text-muted-foreground">
                        No devices assigned — nothing can upload to this
                        event yet.
                    </p>
                )}
        </section>
    );
}
